import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input, Select } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { MultiSelectDropdown } from "@admin/components/atoms/Input/MultiSelect";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  getLocationUsersByid,
  getUser,
  Location,
  Personnel,
  updateUserAndLocations,
  UserRole,
  Wage,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface EditPersonnelModalProps extends ModalProps {
  personnel?: Personnel;
  companyId?: string;
  locations?: Location[];
  wages?: Wage[];
  roles?: UserRole[];
  onSuccess: () => void;
}

export default function EditPersonnelModal({
  personnel,
  companyId,
  locations = [],
  wages = [],
  roles = [],
  onSuccess,
  ...props
}: EditPersonnelModalProps) {
  const groupedLocationsById = locations.reduce(
    (acc, location) => {
      acc[location.locationId] = location;
      return acc;
    },
    {} as Record<string, Location>,
  );

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const editPersonnelSchema = z.object({
    firstName: z.string().min(1, t("common.First name is required")),
    lastName: z.string().min(1, t("common.Last name is required")),
    email: z.string().email(t("common.Invalid email address")),
    userName: z.string().min(1, t("common.Username is required")),
    companyLocationUsers: z
      .array(
        z.object({
          companyLocationId: z.string(),
          userLocationPin: z
            .string()
            .regex(/^\d{4}$/, t("personnel.Invalid pin format")),
          wage: z.coerce
            .number()
            .positive(t("personnel.Wage amount is required")),
          wageType: z.coerce
            .number()
            .positive(t("personnel.Invalid wage type")),
          userRole: z.coerce.number().positive(t("personnel.Invalid role")),
        }),
      )
      .min(1, t("personnel.At least one location must be selected")),
  });
  type EditPersonnelFormData = z.infer<typeof editPersonnelSchema>;

  const initialFormValues: EditPersonnelFormData = {
    firstName: "",
    lastName: "",
    email: "",
    userName: "",
    companyLocationUsers: [],
  };

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm<EditPersonnelFormData>({
    defaultValues: personnel,
    resolver: zodResolver(editPersonnelSchema),
  });

  const { fields, replace } = useFieldArray({
    name: "companyLocationUsers",
    control,
  });

  const handleClose = () => {
    reset(initialFormValues);
    props.onClose?.();
  };

  const mutation = useMutation({
    mutationFn: updateUserAndLocations,
    onSuccess: () => {
      toast.success(t("common.The changes have been saved"));
      handleClose();
      onSuccess();
      queryClient.invalidateQueries({
        queryKey: ["userAndLocations", personnel?.userId],
      });
    },
    onError: () => {
      toast.error(t("common.Unable to save the changes"));
    },
  });

  const editPersonnel = useCallback(
    (data: EditPersonnelFormData) => {
      const companyLocationUsers = data.companyLocationUsers.map((field) => ({
        companyLocationId: field.companyLocationId,
        companyId,
        firstName: data.firstName,
        lastName: data.lastName,
        userName: data.userName,
        userLocationPin: field?.userLocationPin,
        userRole: field?.userRole,
        wageType: field?.wageType,
        wage: field?.wage,
        isDeleted: false,
        userId: personnel?.userId,
      }));

      mutation.mutate({
        userId: personnel?.userId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        userName: data.userName,
        companyLocationUsers,
        companyId,
      });
    },
    [companyId, mutation, personnel?.userId],
  );

  const { data: userAndLocations } = useQuery({
    queryKey: ["userAndLocations", personnel?.userId],
    queryFn: async () => {
      if (personnel) {
        const [user, locations] = await Promise.all([
          getUser(personnel?.userId ?? ""),
          getLocationUsersByid(personnel?.userId ?? ""),
        ]);
        return { user, locations };
      }
      return;
    },
    enabled: !!personnel?.userId,
  });

  useEffect(() => {
    if (personnel && userAndLocations) {
      reset({
        ...personnel,
        companyLocationUsers: userAndLocations.locations.map((location) => ({
          companyLocationId: location.companyLocationId,
          userLocationPin: location.userLocationPin,
          userRole: location.userRole,
          wageType: location.wageType,
          wage: location.wage,
        })),
      });
    }
  }, [personnel, reset, userAndLocations]);

  return (
    <Modal
      {...props}
      onClose={handleClose}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            {t("common.Discard")}
          </Button>
          <Button
            onClick={handleSubmit(editPersonnel)}
            loading={mutation.isPending}
          >
            {t("personnel.Save Personnel")}
          </Button>
        </>
      }
      title={t("personnel.Edit Personnel", {
        firstName: userAndLocations?.user.firstName,
        lastName: userAndLocations?.user.lastName,
      })}
    >
      <form className="flex flex-col">
        <h2 className="font-semibold pb-2.5">
          {t("personnel.Account Information")}
        </h2>
        <input
          hidden
          name="innitialUsername"
          defaultValue={userAndLocations?.user.userName}
        />
        <input hidden name="userId" defaultValue={personnel?.userId} />
        <div className="flex flex-col">
          <Label title={t("common.Username")} required>
            <Input
              {...register("userName")}
              name="userName"
              autoComplete="off"
              defaultValue={userAndLocations?.user.userName}
            />
          </Label>
          <ErrorMessage message={errors?.userName?.message} />
        </div>

        <div className="flex flex-col">
          <Label title={t("personnel.First Name")} required>
            <Input
              {...register("firstName")}
              name="firstName"
              autoComplete="off"
              defaultValue={userAndLocations?.user.firstName}
            />
          </Label>
          <ErrorMessage message={errors?.firstName?.message} />
        </div>

        <div className="flex flex-col">
          <Label title={t("personnel.Last Name")} required>
            <Input
              {...register("lastName")}
              autoComplete="off"
              name="lastName"
              defaultValue={userAndLocations?.user.lastName}
            />
          </Label>
          <ErrorMessage message={errors?.lastName?.message} />
        </div>

        <div className="flex flex-col">
          <Label title={t("common.Email")} required>
            <Input
              {...register("email")}
              name="email"
              defaultValue={userAndLocations?.user.email}
            />
          </Label>
          <ErrorMessage message={errors?.email?.message} />
        </div>

        <div className="border-b py-2.5 border-[#78829D]"></div>
        <h2 className="font-semibold py-2.5">{t("personnel.Connections")}</h2>

        <div className="flex flex-col">
          <Label title={t("personnel.Select Locations")} required>
            <MultiSelectDropdown
              {...register("companyLocationUsers")}
              name="locations"
              variant="filled"
              placeholder={t("personnel.Select Locations")}
              options={locations.map((l) => ({
                value: l.locationId,
                label: l.locationName,
              }))}
              selectedOptions={fields.map((l) => ({
                label:
                  groupedLocationsById[l.companyLocationId]?.locationName ?? "",
                value: l.companyLocationId,
              }))}
              onSelect={(selected) => {
                replace(
                  selected.map(
                    (s) =>
                      fields.find((f) => f.companyLocationId === s.value) || {
                        companyLocationId: s.value as string,
                        userLocationPin: "1234",
                        wage: 0,
                        wageType: 1,
                        userRole: 3,
                      },
                  ),
                );
              }}
            />
          </Label>
          <ErrorMessage message={errors?.companyLocationUsers?.message} />
        </div>

        {fields.map((field, index) => {
          const fieldErrors = errors?.companyLocationUsers?.[index];
          const defaultLocation = userAndLocations?.locations.find(
            (l) => l.companyLocationId === field.companyLocationId,
          );

          return (
            <div key={field.id}>
              <div className="border-b py-2.5 border-[#CAC4D0]"></div>
              <h3 className="py-2.5">
                {groupedLocationsById[field.companyLocationId].locationName}
              </h3>
              <Label title={t("personnel.PIN Code")} required>
                <Input
                  {...register(`companyLocationUsers.${index}.userLocationPin`)}
                  defaultValue={defaultLocation?.userLocationPin}
                  type="number"
                />
              </Label>
              <ErrorMessage message={fieldErrors?.userLocationPin?.message} />

              <Label title={t("personnel.Role in this Location")} required>
                <Select
                  {...register(`companyLocationUsers.${index}.userRole`)}
                  defaultValue={defaultLocation?.userRole}
                  options={roles
                    .filter((r) => r.id === "3" || r.id === "5")
                    .map((r) => ({
                      value: Number(r.id),
                      label:
                        {
                          3: t("personnel.Manager"),
                          5: t("personnel.Employee"),
                        }[r.id] ?? r.label,
                    }))}
                />
              </Label>
              <ErrorMessage message={fieldErrors?.userRole?.message} />

              <Label title={t("personnel.Wage Type")} required>
                <Select
                  {...register(`companyLocationUsers.${index}.wageType`)}
                  defaultValue={defaultLocation?.wageType}
                  options={wages.map((w) => ({
                    value: w.id,
                    label:
                      {
                        1: t("shift.Hourly Wage"),
                        3: t("shift.Monthly Salary"),
                      }[w.id] ?? w.name,
                  }))}
                />
              </Label>
              <ErrorMessage message={fieldErrors?.wageType?.message} />

              <Label title={t("personnel.Wage Amount (DKK)")} required>
                <Input
                  {...register(`companyLocationUsers.${index}.wage`)}
                  defaultValue={defaultLocation?.wage}
                  type="number"
                />
              </Label>
              <ErrorMessage message={fieldErrors?.wage?.message} />
            </div>
          );
        })}
      </form>
    </Modal>
  );
}
